import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, Loader2, Sparkles } from "lucide-react";
import { LOGIN, REGISTER } from "@/constants/testIds";
import { PublicStats } from "@/components/PublicStats";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWith } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { name, email, password };
      const { data } = await api.post(path, body);
      loginWith(data.token, data.user);
      navigate("/inicio");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md vc-fade-up">
        <div className="mb-6 flex flex-col items-center text-center">
          <img
  src="/images/logo-apoiaconcursos.jpeg"
  alt="ApoiaConcursos"
  className="mb-3 h-auto w-48 object-contain"
/>
          <p className="mt-1 text-sm font-bold uppercase tracking-wide text-slate-300">
  SEU APOIO PARA APROVAÇÃO
</p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-slate-800 p-1">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`rounded-lg py-2 text-sm font-bold transition-colors ${
                  mode === m ? "bg-emerald-500 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {m === "login" ? "Entrar" : "Criar conta"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-300">Nome</label>
                <input
                  data-testid={REGISTER.nameInput}
                  value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
                  placeholder="Seu nome"
                />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-300">E-mail</label>
              <input
                data-testid={mode === "login" ? LOGIN.emailInput : REGISTER.emailInput}
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
                placeholder="voce@email.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-300">Senha</label>
              <div className="relative">
                <input
                  data-testid={mode === "login" ? LOGIN.passwordInput : REGISTER.passwordInput}
                  type={showPw ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 pr-10 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
                  placeholder="Mínimo 6 caracteres"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  data-testid="auth-toggle-password">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {mode === "login" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/esqueci-senha")}
                  className="text-xs font-semibold text-emerald-400 hover:text-emerald-300"
                >
                  Esqueci minha senha
                </button>
              </div>
            )}

            {error && (
              <p data-testid="auth-error" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm font-medium text-red-400">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid={mode === "login" ? LOGIN.submitButton : REGISTER.submitButton}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-70"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {mode === "login" ? "Entrar" : "Criar minha conta"}
            </button>
          </form>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" />
            7 dias grátis no Premium para novos assinantes.
          </div>
        </div>

        <PublicStats />
      </div>
    </div>
  );
}
